import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { 
  ArrowLeft, Save, Plus, Eye, Trash2, GripVertical,
  Type, Image, Layout, MessageSquare, List, Columns,
  Video, Quote, MapPin, Mail, ChevronDown, ChevronUp,
  Settings, X, Check, Gift, DollarSign, Star, Calendar,
  Share2, Clock, HelpCircle, MousePointer, Minus, ImageIcon,
  Music, Play
} from 'lucide-react';
import MediaLibraryPicker from '@/components/admin/MediaLibraryPicker';
import TemplatePicker from '@/components/admin/TemplatePicker';
import { motion, AnimatePresence } from 'framer-motion';
import { adminFetch, isAdminAuthenticated } from '@/utils/adminAuth';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Block type definitions
const BLOCK_TYPES = [
  { type: 'hero', label: 'Hero Section', icon: Layout, description: 'Large banner with title and CTA' },
  { type: 'text', label: 'Text/Paragraph', icon: Type, description: 'Rich text content block' },
  { type: 'image', label: 'Image', icon: Image, description: 'Single image with caption' },
  { type: 'media-gallery', label: 'Media Gallery', icon: ImageIcon, description: 'Images from Media Library' },
  { type: 'gallery', label: 'Image Gallery', icon: Columns, description: 'Grid of images' },
  { type: 'video', label: 'Video Embed', icon: Video, description: 'YouTube or Vimeo' },
  { type: 'audio', label: 'Audio Player', icon: Music, description: 'Audio from Media Library' },
  { type: 'cta', label: 'Call to Action', icon: MessageSquare, description: 'Button with text' },
  { type: 'button-group', label: 'Button Group', icon: MousePointer, description: 'Multiple buttons in a row' },
  { type: 'faq', label: 'FAQ/Accordion', icon: HelpCircle, description: 'Collapsible Q&A sections' },
  { type: 'quote', label: 'Testimonial', icon: Quote, description: 'Quote or review' },
  { type: 'testimonials', label: 'Testimonials Grid', icon: Star, description: 'Multiple reviews' },
  { type: 'pricing', label: 'Pricing Table', icon: DollarSign, description: 'Pricing plans comparison' },
  { type: 'gift', label: 'Gift Card', icon: Gift, description: 'Gift card or voucher display' },
  { type: 'event', label: 'Event Calendar', icon: Calendar, description: 'Upcoming events list' },
  { type: 'countdown', label: 'Countdown Timer', icon: Clock, description: 'Timer to a specific date' },
  { type: 'social', label: 'Social Links', icon: Share2, description: 'Social media buttons' },
  { type: 'contact', label: 'Contact Info', icon: Mail, description: 'Contact details' },
  { type: 'divider', label: 'Divider', icon: Minus, description: 'Horizontal line separator' },
  { type: 'spacer', label: 'Spacer', icon: ChevronDown, description: 'Add vertical space' },
];

// Default content for each block type
const getDefaultContent = (type) => {
  switch (type) {
    case 'hero':
      return {
        title: 'Your Hero Title',
        subtitle: 'Add a compelling subtitle here',
        buttonText: 'Learn More',
        buttonLink: '#',
        backgroundImage: '',
        alignment: 'left'
      };
    case 'text':
      return {
        content: '<p>Add your content here. You can write paragraphs, add <strong>bold</strong> or <em>italic</em> text.</p>',
        alignment: 'left',
        fontSize: 'base'
      };
    case 'image':
      return {
        src: '',
        alt: '',
        caption: '',
        size: 'full',
        link: ''
      };
    case 'media-gallery':
      return {
        images: [],
        columns: 3,
        gap: 4,
        showCaptions: true
      };
    case 'gallery':
      return {
        images: [],
        columns: 3
      };
    case 'video':
      return {
        url: '',
        title: '',
        autoplay: false
      };
    case 'audio':
      return {
        src: '',
        title: '',
        showControls: true
      };
    case 'cta':
      return {
        title: 'Ready to get started?',
        description: 'Add a compelling call to action',
        buttonText: 'Get Started',
        buttonLink: '#',
        style: 'primary'
      };
    case 'button-group':
      return {
        buttons: [
          { text: 'Primary Button', link: '#', style: 'primary' },
          { text: 'Secondary Button', link: '#', style: 'secondary' }
        ],
        alignment: 'center'
      };
    case 'faq':
      return {
        title: 'Frequently Asked Questions',
        items: [
          { question: 'Sample question?', answer: 'Sample answer.' }
        ]
      };
    case 'quote':
      return {
        text: 'Add your quote here',
        author: 'Author Name',
        role: 'Position',
        avatar: '',
        rating: 5
      };
    case 'testimonials':
      return {
        title: 'What Our Customers Say',
        items: [
          { text: 'Great experience!', author: 'Customer Name', role: 'Customer', rating: 5 }
        ],
        columns: 3
      };
    case 'pricing':
      return {
        title: 'Choose Your Plan',
        plans: [
          { 
            name: 'Basic', 
            price: '9.99', 
            period: 'month',
            features: ['Feature 1', 'Feature 2'],
            buttonText: 'Get Started',
            buttonLink: '#',
            highlighted: false
          },
          { 
            name: 'Pro', 
            price: '19.99', 
            period: 'month',
            features: ['All Basic features', 'Feature 3', 'Feature 4'],
            buttonText: 'Get Started',
            buttonLink: '#',
            highlighted: true
          }
        ]
      };
    case 'gift':
      return {
        title: 'Gift Card',
        description: 'Give the gift of music',
        image: '',
        amounts: ['25', '50', '100'],
        buttonText: 'Purchase Gift Card',
        buttonLink: '#'
      };
    case 'event':
      return {
        title: 'Upcoming Events',
        events: [
          {
            name: 'Event Name',
            date: new Date().toISOString().split('T')[0],
            time: '7:00 PM',
            location: 'Event Location',
            description: 'Event description',
            link: '#'
          }
        ]
      };
    case 'countdown':
      return {
        title: 'Coming Soon',
        targetDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        showDays: true,
        showHours: true,
        showMinutes: true,
        showSeconds: true,
        message: 'Something exciting is coming!'
      };
    case 'social':
      return {
        title: 'Follow Us',
        links: [
          { platform: 'facebook', url: '' },
          { platform: 'instagram', url: '' },
          { platform: 'youtube', url: '' },
          { platform: 'twitter', url: '' }
        ],
        style: 'icons'
      };
    case 'contact':
      return {
        email: '',
        phone: '',
        address: '',
        showMap: false
      };
    case 'divider':
      return {
        style: 'solid',
        color: 'gray',
        width: 'full',
        spacing: 8
      };
    case 'spacer':
      return {
        height: 60
      };
    default:
      return {};
  }
};

// Sortable Block Component
const SortableBlock = ({ block, onEdit, onDelete, isEditing, setEditingBlock }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: block.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const blockType = BLOCK_TYPES.find(t => t.type === block.type);
  const Icon = blockType?.icon || Type;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-card border rounded-xl overflow-hidden transition-all ${
        isDragging ? 'border-primary shadow-lg' : 'border-border/50 hover:border-primary/30'
      }`}
    >
      {/* Block Header */}
      <div className="flex items-center gap-3 p-4 border-b border-border/50 bg-background/50">
        <button
          {...attributes}
          {...listeners}
          className="p-1 text-muted-foreground hover:text-foreground cursor-grab active:cursor-grabbing"
        >
          <GripVertical className="w-5 h-5" />
        </button>
        
        <div className="flex items-center gap-2 flex-1">
          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon className="w-4 h-4 text-primary" />
          </div>
          <span className="font-medium text-foreground">{blockType?.label || block.type}</span>
        </div>

        <div className="flex items-center gap-1">
          <button
            onClick={() => setEditingBlock(isEditing ? null : block.id)}
            className={`p-2 rounded-lg transition-colors ${
              isEditing 
                ? 'bg-primary text-primary-foreground' 
                : 'text-muted-foreground hover:text-foreground hover:bg-background'
            }`}
          >
            <Settings className="w-4 h-4" />
          </button>
          <button
            onClick={() => onDelete(block.id)}
            className="p-2 text-muted-foreground hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Block Preview */}
      <div className="p-4">
        <BlockPreview block={block} />
      </div>

      {/* Block Editor */}
      <AnimatePresence>
        {isEditing && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="border-t border-border/50 overflow-hidden"
          >
            <div className="p-4 bg-background/50">
              <BlockEditor block={block} onUpdate={onEdit} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Block Preview Component
const BlockPreview = ({ block }) => {
  const { type, content } = block;

  switch (type) {
    case 'hero':
      return (
        <div className="text-center py-4">
          <h3 className="text-xl font-bold text-foreground">{content.title}</h3>
          <p className="text-muted-foreground text-sm mt-1">{content.subtitle}</p>
          {content.buttonText && (
            <span className="inline-block mt-2 px-3 py-1 bg-primary/20 text-primary text-xs rounded-full">
              {content.buttonText}
            </span>
          )}
        </div>
      );
    case 'text':
      return (
        <div 
          className="text-sm text-muted-foreground prose prose-sm max-w-none"
          dangerouslySetInnerHTML={{ __html: content.content?.substring(0, 200) + '...' }}
        />
      );
    case 'image':
      return content.src ? (
        <img loading="lazy" decoding="async" src={content.src} alt={content.alt} className="max-h-32 mx-auto rounded-lg" />
      ) : (
        <div className="h-24 bg-stone-800 rounded-lg flex items-center justify-center">
          <Image className="w-8 h-8 text-muted-foreground" />
        </div>
      );
    case 'gallery':
      return (
        <div className="flex gap-2 justify-center">
          {(content.images || []).slice(0, 4).map((img, i) => (
            <div key={i} className="w-16 h-16 bg-stone-800 rounded-lg overflow-hidden">
              {img.src && <img loading="lazy" decoding="async" src={img.src} alt="" className="w-full h-full object-cover" />}
            </div>
          ))}
          {(!content.images || content.images.length === 0) && (
            <div className="text-sm text-muted-foreground">No images added</div>
          )}
        </div>
      );
    case 'cta':
      return (
        <div className="text-center py-2">
          <p className="font-medium text-foreground">{content.title}</p>
          <span className="inline-block mt-2 px-3 py-1 bg-primary text-primary-foreground text-xs rounded-full">
            {content.buttonText}
          </span>
        </div>
      );
    case 'faq':
      return (
        <div className="space-y-1">
          {(content.items || []).slice(0, 2).map((item, i) => (
            <div key={i} className="text-sm">
              <span className="font-medium text-foreground">Q: {item.question}</span>
            </div>
          ))}
          {content.items?.length > 2 && (
            <p className="text-xs text-muted-foreground">+{content.items.length - 2} more</p>
          )}
        </div>
      );
    case 'quote':
      return (
        <blockquote className="italic text-muted-foreground text-sm border-l-2 border-primary pl-3">
          "{content.text}"
          <footer className="text-xs mt-1 not-italic">— {content.author}</footer>
        </blockquote>
      );
    case 'media-gallery':
      return (
        <div className="flex gap-2 justify-center flex-wrap">
          {(content.images || []).slice(0, 4).map((img, i) => (
            <div key={i} className="w-16 h-16 bg-stone-800 rounded-lg overflow-hidden">
              <img loading="lazy" decoding="async" src={img.url || img.src} alt="" className="w-full h-full object-cover" />
            </div>
          ))}
          {(!content.images || content.images.length === 0) && (
            <div className="text-sm text-muted-foreground">No media selected</div>
          )}
          {content.images?.length > 4 && (
            <div className="w-16 h-16 bg-stone-800 rounded-lg flex items-center justify-center text-xs text-muted-foreground">
              +{content.images.length - 4}
            </div>
          )}
        </div>
      );
    case 'audio':
      return (
        <div className="flex items-center gap-3 p-3 bg-stone-800 rounded-lg">
          <Music className="w-6 h-6 text-primary" />
          <span className="text-sm text-foreground">{content.title || 'Audio file'}</span>
        </div>
      );
    case 'button-group':
      return (
        <div className="flex gap-2 justify-center flex-wrap">
          {(content.buttons || []).map((btn, i) => (
            <span key={i} className={`px-3 py-1 rounded-full text-xs ${
              btn.style === 'primary' ? 'bg-primary text-black' : 'bg-zinc-700 text-white'
            }`}>
              {btn.text}
            </span>
          ))}
        </div>
      );
    case 'testimonials':
      return (
        <div className="space-y-2">
          <p className="font-medium text-foreground text-sm">{content.title}</p>
          <p className="text-xs text-muted-foreground">{content.items?.length || 0} testimonials</p>
        </div>
      );
    case 'pricing':
      return (
        <div className="flex gap-2 justify-center">
          {(content.plans || []).map((plan, i) => (
            <div key={i} className={`px-3 py-2 rounded-lg text-center ${
              plan.highlighted ? 'bg-primary/20 border border-primary' : 'bg-stone-800'
            }`}>
              <p className="text-xs font-medium text-foreground">{plan.name}</p>
              <p className="text-sm font-bold text-primary">${plan.price}</p>
            </div>
          ))}
        </div>
      );
    case 'gift':
      return (
        <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-primary/20 to-amber-500/20 rounded-lg">
          <Gift className="w-6 h-6 text-primary" />
          <div>
            <p className="font-medium text-foreground text-sm">{content.title}</p>
            <p className="text-xs text-muted-foreground">{content.amounts?.join(', ') || 'No amounts set'}</p>
          </div>
        </div>
      );
    case 'event':
      return (
        <div className="space-y-2">
          <p className="font-medium text-foreground text-sm">{content.title}</p>
          {(content.events || []).slice(0, 2).map((evt, i) => (
            <div key={i} className="flex items-center gap-2 text-xs text-muted-foreground">
              <Calendar className="w-3 h-3" />
              <span>{evt.name} - {evt.date}</span>
            </div>
          ))}
        </div>
      );
    case 'countdown':
      return (
        <div className="text-center py-2">
          <p className="font-medium text-foreground text-sm">{content.title}</p>
          <div className="flex gap-2 justify-center mt-2">
            {['Days', 'Hours', 'Min', 'Sec'].map((unit, i) => (
              <div key={i} className="px-2 py-1 bg-stone-800 rounded text-xs">
                <span className="text-primary font-bold">00</span>
                <span className="text-muted-foreground ml-1">{unit}</span>
              </div>
            ))}
          </div>
        </div>
      );
    case 'social':
      return (
        <div className="flex gap-2 justify-center">
          {(content.links || []).map((link, i) => (
            <div key={i} className="w-8 h-8 bg-stone-800 rounded-full flex items-center justify-center">
              <Share2 className="w-4 h-4 text-muted-foreground" />
            </div>
          ))}
        </div>
      );
    case 'divider':
      return (
        <div className={`border-t border-${content.color || 'gray'}-500 my-2`} style={{ width: content.width === 'full' ? '100%' : '50%', margin: '0 auto' }} />
      );
    case 'spacer':
      return (
        <div className="text-center text-xs text-muted-foreground">
          {content.height}px spacer
        </div>
      );
    default:
      return <div className="text-sm text-muted-foreground">Block content</div>;
  }
};

// ============ SPECIALIZED BLOCK EDITORS ============

// Media Gallery Editor with Library Picker
const MediaGalleryEditor = ({ content, handleChange }) => {
  const [showPicker, setShowPicker] = useState(false);

  const handleMediaSelect = (selected) => {
    const newImages = Array.isArray(selected) ? selected : [selected];
    handleChange('images', [...(content.images || []), ...newImages]);
  };

  const removeImage = (index) => {
    const images = content.images.filter((_, i) => i !== index);
    handleChange('images', images);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <label className="text-xs font-bold text-muted-foreground">Images from Media Library</label>
        <button
          onClick={() => setShowPicker(true)}
          className="px-3 py-1 bg-primary/20 text-primary text-sm rounded-lg hover:bg-primary/30"
        >
          + Add from Library
        </button>
      </div>
      
      <div className="grid grid-cols-4 gap-2">
        {(content.images || []).map((img, i) => (
          <div key={i} className="relative group aspect-square rounded-lg overflow-hidden bg-stone-800">
            <img loading="lazy" decoding="async" src={img.url || img.src} alt="" className="w-full h-full object-cover" />
            <button
              onClick={() => removeImage(i)}
              className="absolute top-1 right-1 p-1 bg-red-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <X className="w-3 h-3 text-white" />
            </button>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-bold text-muted-foreground mb-1">Columns</label>
          <select
            value={content.columns}
            onChange={(e) => handleChange('columns', parseInt(e.target.value))}
            className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground"
          >
            <option value={2}>2 Columns</option>
            <option value={3}>3 Columns</option>
            <option value={4}>4 Columns</option>
          </select>
        </div>
        <div className="flex items-center gap-2 pt-6">
          <input
            type="checkbox"
            checked={content.showCaptions}
            onChange={(e) => handleChange('showCaptions', e.target.checked)}
            className="rounded"
          />
          <label className="text-sm text-foreground">Show Captions</label>
        </div>
      </div>

      <MediaLibraryPicker
        isOpen={showPicker}
        onClose={() => setShowPicker(false)}
        onSelect={handleMediaSelect}
        allowedTypes={['image']}
        multiple={true}
      />
    </div>
  );
};

// Audio Block Editor
const AudioBlockEditor = ({ content, handleChange }) => {
  const [showPicker, setShowPicker] = useState(false);

  const handleMediaSelect = (selected) => {
    handleChange('src', selected.url);
    handleChange('title', selected.filename || content.title);
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-xs font-bold text-muted-foreground mb-1">Audio File</label>
        <div className="flex gap-2">
          <input
            type="text"
            value={content.src}
            onChange={(e) => handleChange('src', e.target.value)}
            placeholder="Audio URL..."
            className="flex-1 px-3 py-2 bg-background border border-border rounded-lg text-foreground"
          />
          <button
            onClick={() => setShowPicker(true)}
            className="px-3 py-2 bg-primary/20 text-primary rounded-lg hover:bg-primary/30"
          >
            Library
          </button>
        </div>
      </div>
      <div>
        <label className="block text-xs font-bold text-muted-foreground mb-1">Title</label>
        <input
          type="text"
          value={content.title}
          onChange={(e) => handleChange('title', e.target.value)}
          className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground"
        />
      </div>

      <MediaLibraryPicker
        isOpen={showPicker}
        onClose={() => setShowPicker(false)}
        onSelect={handleMediaSelect}
        allowedTypes={['audio']}
        multiple={false}
      />
    </div>
  );
};

// Button Group Editor
const ButtonGroupEditor = ({ content, handleChange }) => {
  const addButton = () => {
    const buttons = [...(content.buttons || []), { text: 'New Button', link: '#', style: 'secondary' }];
    handleChange('buttons', buttons);
  };

  const updateButton = (index, field, value) => {
    const buttons = [...content.buttons];
    buttons[index] = { ...buttons[index], [field]: value };
    handleChange('buttons', buttons);
  };

  const removeButton = (index) => {
    const buttons = content.buttons.filter((_, i) => i !== index);
    handleChange('buttons', buttons);
  };

  return (
    <div className="space-y-4">
      {(content.buttons || []).map((btn, i) => (
        <div key={i} className="p-3 bg-background rounded-lg border border-border space-y-2">
          <div className="flex gap-2">
            <input
              type="text"
              value={btn.text}
              onChange={(e) => updateButton(i, 'text', e.target.value)}
              placeholder="Button text"
              className="flex-1 px-3 py-2 bg-card border border-border rounded-lg text-foreground text-sm"
            />
            <select
              value={btn.style}
              onChange={(e) => updateButton(i, 'style', e.target.value)}
              className="px-3 py-2 bg-card border border-border rounded-lg text-foreground text-sm"
            >
              <option value="primary">Primary</option>
              <option value="secondary">Secondary</option>
              <option value="outline">Outline</option>
            </select>
            <button onClick={() => removeButton(i)} className="p-2 text-red-500">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
          <input
            type="text"
            value={btn.link}
            onChange={(e) => updateButton(i, 'link', e.target.value)}
            placeholder="Button link"
            className="w-full px-3 py-2 bg-card border border-border rounded-lg text-foreground text-sm"
          />
        </div>
      ))}
      <button
        onClick={addButton}
        className="w-full py-2 border-2 border-dashed border-border hover:border-primary/50 rounded-lg text-muted-foreground"
      >
        + Add Button
      </button>
    </div>
  );
};

// Testimonials Editor
const TestimonialsEditor = ({ content, handleChange }) => {
  const addTestimonial = () => {
    const items = [...(content.items || []), { text: '', author: '', role: '', rating: 5 }];
    handleChange('items', items);
  };

  const updateItem = (index, field, value) => {
    const items = [...content.items];
    items[index] = { ...items[index], [field]: value };
    handleChange('items', items);
  };

  const removeItem = (index) => {
    const items = content.items.filter((_, i) => i !== index);
    handleChange('items', items);
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-xs font-bold text-muted-foreground mb-1">Section Title</label>
        <input
          type="text"
          value={content.title}
          onChange={(e) => handleChange('title', e.target.value)}
          className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground"
        />
      </div>
      
      {(content.items || []).map((item, i) => (
        <div key={i} className="p-3 bg-background rounded-lg border border-border space-y-2">
          <textarea
            value={item.text}
            onChange={(e) => updateItem(i, 'text', e.target.value)}
            placeholder="Testimonial text..."
            rows={2}
            className="w-full px-3 py-2 bg-card border border-border rounded-lg text-foreground text-sm"
          />
          <div className="grid grid-cols-3 gap-2">
            <input
              type="text"
              value={item.author}
              onChange={(e) => updateItem(i, 'author', e.target.value)}
              placeholder="Name"
              className="px-3 py-2 bg-card border border-border rounded-lg text-foreground text-sm"
            />
            <input
              type="text"
              value={item.role}
              onChange={(e) => updateItem(i, 'role', e.target.value)}
              placeholder="Role"
              className="px-3 py-2 bg-card border border-border rounded-lg text-foreground text-sm"
            />
            <div className="flex items-center gap-1">
              {[1,2,3,4,5].map(star => (
                <button
                  key={star}
                  onClick={() => updateItem(i, 'rating', star)}
                  className={star <= item.rating ? 'text-yellow-500' : 'text-gray-500'}
                >
                  <Star className="w-4 h-4 fill-current" />
                </button>
              ))}
            </div>
          </div>
          <button onClick={() => removeItem(i)} className="text-xs text-red-500">Remove</button>
        </div>
      ))}
      <button onClick={addTestimonial} className="w-full py-2 border-2 border-dashed border-border rounded-lg text-muted-foreground">
        + Add Testimonial
      </button>
    </div>
  );
};

// Pricing Editor
const PricingEditor = ({ content, handleChange }) => {
  const addPlan = () => {
    const plans = [...(content.plans || []), { name: 'New Plan', price: '0', period: 'month', features: [], buttonText: 'Get Started', buttonLink: '#', highlighted: false }];
    handleChange('plans', plans);
  };

  const updatePlan = (index, field, value) => {
    const plans = [...content.plans];
    plans[index] = { ...plans[index], [field]: value };
    handleChange('plans', plans);
  };

  const addFeature = (planIndex) => {
    const plans = [...content.plans];
    plans[planIndex].features = [...(plans[planIndex].features || []), 'New feature'];
    handleChange('plans', plans);
  };

  const updateFeature = (planIndex, featureIndex, value) => {
    const plans = [...content.plans];
    plans[planIndex].features[featureIndex] = value;
    handleChange('plans', plans);
  };

  const removePlan = (index) => {
    const plans = content.plans.filter((_, i) => i !== index);
    handleChange('plans', plans);
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-xs font-bold text-muted-foreground mb-1">Section Title</label>
        <input
          type="text"
          value={content.title}
          onChange={(e) => handleChange('title', e.target.value)}
          className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground"
        />
      </div>
      
      {(content.plans || []).map((plan, i) => (
        <div key={i} className={`p-4 rounded-lg border ${plan.highlighted ? 'border-primary bg-primary/5' : 'border-border bg-background'}`}>
          <div className="grid grid-cols-2 gap-2 mb-3">
            <input
              type="text"
              value={plan.name}
              onChange={(e) => updatePlan(i, 'name', e.target.value)}
              placeholder="Plan name"
              className="px-3 py-2 bg-card border border-border rounded-lg text-foreground text-sm"
            />
            <div className="flex gap-1">
              <span className="px-2 py-2 text-muted-foreground">$</span>
              <input
                type="text"
                value={plan.price}
                onChange={(e) => updatePlan(i, 'price', e.target.value)}
                placeholder="Price"
                className="flex-1 px-3 py-2 bg-card border border-border rounded-lg text-foreground text-sm"
              />
              <select
                value={plan.period}
                onChange={(e) => updatePlan(i, 'period', e.target.value)}
                className="px-2 py-2 bg-card border border-border rounded-lg text-foreground text-sm"
              >
                <option value="month">/mo</option>
                <option value="year">/yr</option>
                <option value="once">once</option>
              </select>
            </div>
          </div>
          <div className="space-y-1 mb-3">
            {(plan.features || []).map((feature, fi) => (
              <input
                key={fi}
                type="text"
                value={feature}
                onChange={(e) => updateFeature(i, fi, e.target.value)}
                className="w-full px-3 py-1 bg-card border border-border rounded text-foreground text-xs"
              />
            ))}
            <button onClick={() => addFeature(i)} className="text-xs text-primary">+ Add feature</button>
          </div>
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={plan.highlighted}
                onChange={(e) => updatePlan(i, 'highlighted', e.target.checked)}
              />
              Highlight this plan
            </label>
            <button onClick={() => removePlan(i)} className="text-xs text-red-500">Remove</button>
          </div>
        </div>
      ))}
      <button onClick={addPlan} className="w-full py-2 border-2 border-dashed border-border rounded-lg text-muted-foreground">
        + Add Pricing Plan
      </button>
    </div>
  );
};

// Gift Card Editor
const GiftCardEditor = ({ content, handleChange }) => {
  const [showPicker, setShowPicker] = useState(false);

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-xs font-bold text-muted-foreground mb-1">Title</label>
        <input
          type="text"
          value={content.title}
          onChange={(e) => handleChange('title', e.target.value)}
          className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground"
        />
      </div>
      <div>
        <label className="block text-xs font-bold text-muted-foreground mb-1">Description</label>
        <textarea
          value={content.description}
          onChange={(e) => handleChange('description', e.target.value)}
          rows={2}
          className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground"
        />
      </div>
      <div>
        <label className="block text-xs font-bold text-muted-foreground mb-1">Gift Card Image</label>
        <div className="flex gap-2">
          <input
            type="text"
            value={content.image}
            onChange={(e) => handleChange('image', e.target.value)}
            placeholder="Image URL..."
            className="flex-1 px-3 py-2 bg-background border border-border rounded-lg text-foreground"
          />
          <button
            onClick={() => setShowPicker(true)}
            className="px-3 py-2 bg-primary/20 text-primary rounded-lg"
          >
            Library
          </button>
        </div>
      </div>
      <div>
        <label className="block text-xs font-bold text-muted-foreground mb-1">Amount Options (comma-separated)</label>
        <input
          type="text"
          value={(content.amounts || []).join(', ')}
          onChange={(e) => handleChange('amounts', e.target.value.split(',').map(a => a.trim()))}
          placeholder="25, 50, 100"
          className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-bold text-muted-foreground mb-1">Button Text</label>
          <input
            type="text"
            value={content.buttonText}
            onChange={(e) => handleChange('buttonText', e.target.value)}
            className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground"
          />
        </div>
        <div>
          <label className="block text-xs font-bold text-muted-foreground mb-1">Button Link</label>
          <input
            type="text"
            value={content.buttonLink}
            onChange={(e) => handleChange('buttonLink', e.target.value)}
            className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground"
          />
        </div>
      </div>

      <MediaLibraryPicker
        isOpen={showPicker}
        onClose={() => setShowPicker(false)}
        onSelect={(img) => handleChange('image', img.url)}
        allowedTypes={['image']}
        multiple={false}
      />
    </div>
  );
};

// Event Calendar Editor
const EventEditor = ({ content, handleChange }) => {
  const addEvent = () => {
    const events = [...(content.events || []), { name: '', date: '', time: '', location: '', description: '', link: '' }];
    handleChange('events', events);
  };

  const updateEvent = (index, field, value) => {
    const events = [...content.events];
    events[index] = { ...events[index], [field]: value };
    handleChange('events', events);
  };

  const removeEvent = (index) => {
    const events = content.events.filter((_, i) => i !== index);
    handleChange('events', events);
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-xs font-bold text-muted-foreground mb-1">Section Title</label>
        <input
          type="text"
          value={content.title}
          onChange={(e) => handleChange('title', e.target.value)}
          className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground"
        />
      </div>
      
      {(content.events || []).map((event, i) => (
        <div key={i} className="p-3 bg-background rounded-lg border border-border space-y-2">
          <input
            type="text"
            value={event.name}
            onChange={(e) => updateEvent(i, 'name', e.target.value)}
            placeholder="Event name"
            className="w-full px-3 py-2 bg-card border border-border rounded-lg text-foreground text-sm"
          />
          <div className="grid grid-cols-3 gap-2">
            <input
              type="date"
              value={event.date}
              onChange={(e) => updateEvent(i, 'date', e.target.value)}
              className="px-3 py-2 bg-card border border-border rounded-lg text-foreground text-sm"
            />
            <input
              type="time"
              value={event.time}
              onChange={(e) => updateEvent(i, 'time', e.target.value)}
              className="px-3 py-2 bg-card border border-border rounded-lg text-foreground text-sm"
            />
            <input
              type="text"
              value={event.location}
              onChange={(e) => updateEvent(i, 'location', e.target.value)}
              placeholder="Location"
              className="px-3 py-2 bg-card border border-border rounded-lg text-foreground text-sm"
            />
          </div>
          <textarea
            value={event.description}
            onChange={(e) => updateEvent(i, 'description', e.target.value)}
            placeholder="Event description..."
            rows={2}
            className="w-full px-3 py-2 bg-card border border-border rounded-lg text-foreground text-sm"
          />
          <button onClick={() => removeEvent(i)} className="text-xs text-red-500">Remove Event</button>
        </div>
      ))}
      <button onClick={addEvent} className="w-full py-2 border-2 border-dashed border-border rounded-lg text-muted-foreground">
        + Add Event
      </button>
    </div>
  );
};

// Countdown Timer Editor
const CountdownEditor = ({ content, handleChange }) => {
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-xs font-bold text-muted-foreground mb-1">Title</label>
        <input
          type="text"
          value={content.title}
          onChange={(e) => handleChange('title', e.target.value)}
          className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground"
        />
      </div>
      <div>
        <label className="block text-xs font-bold text-muted-foreground mb-1">Target Date</label>
        <input
          type="date"
          value={content.targetDate}
          onChange={(e) => handleChange('targetDate', e.target.value)}
          className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground"
        />
      </div>
      <div>
        <label className="block text-xs font-bold text-muted-foreground mb-1">Message</label>
        <input
          type="text"
          value={content.message}
          onChange={(e) => handleChange('message', e.target.value)}
          className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground"
        />
      </div>
      <div className="grid grid-cols-4 gap-2">
        {['showDays', 'showHours', 'showMinutes', 'showSeconds'].map(field => (
          <label key={field} className="flex items-center gap-1 text-xs">
            <input
              type="checkbox"
              checked={content[field]}
              onChange={(e) => handleChange(field, e.target.checked)}
            />
            {field.replace('show', '')}
          </label>
        ))}
      </div>
    </div>
  );
};

// Social Links Editor
const SocialLinksEditor = ({ content, handleChange }) => {
  const platforms = ['facebook', 'instagram', 'youtube', 'twitter', 'tiktok', 'linkedin', 'spotify'];
  
  const updateLink = (index, field, value) => {
    const links = [...content.links];
    links[index] = { ...links[index], [field]: value };
    handleChange('links', links);
  };

  const addLink = () => {
    const links = [...(content.links || []), { platform: 'facebook', url: '' }];
    handleChange('links', links);
  };

  const removeLink = (index) => {
    const links = content.links.filter((_, i) => i !== index);
    handleChange('links', links);
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-xs font-bold text-muted-foreground mb-1">Title (optional)</label>
        <input
          type="text"
          value={content.title}
          onChange={(e) => handleChange('title', e.target.value)}
          className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground"
        />
      </div>
      
      {(content.links || []).map((link, i) => (
        <div key={i} className="flex gap-2">
          <select
            value={link.platform}
            onChange={(e) => updateLink(i, 'platform', e.target.value)}
            className="px-3 py-2 bg-background border border-border rounded-lg text-foreground"
          >
            {platforms.map(p => (
              <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
            ))}
          </select>
          <input
            type="text"
            value={link.url}
            onChange={(e) => updateLink(i, 'url', e.target.value)}
            placeholder="Profile URL..."
            className="flex-1 px-3 py-2 bg-background border border-border rounded-lg text-foreground"
          />
          <button onClick={() => removeLink(i)} className="p-2 text-red-500">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ))}
      <button onClick={addLink} className="w-full py-2 border-2 border-dashed border-border rounded-lg text-muted-foreground">
        + Add Social Link
      </button>
    </div>
  );
};

// Block Editor Component
const BlockEditor = ({ block, onUpdate }) => {
  const [content, setContent] = useState(block.content);

  const handleChange = (key, value) => {
    const newContent = { ...content, [key]: value };
    setContent(newContent);
    onUpdate(block.id, { content: newContent });
  };

  const handleFaqAdd = () => {
    const items = [...(content.items || []), { question: 'New question?', answer: 'Answer here.' }];
    handleChange('items', items);
  };

  const handleFaqUpdate = (index, field, value) => {
    const items = [...content.items];
    items[index] = { ...items[index], [field]: value };
    handleChange('items', items);
  };

  const handleFaqDelete = (index) => {
    const items = content.items.filter((_, i) => i !== index);
    handleChange('items', items);
  };

  switch (block.type) {
    case 'hero':
      return (
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-bold text-muted-foreground mb-1">Title</label>
            <input
              type="text"
              value={content.title}
              onChange={(e) => handleChange('title', e.target.value)}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-muted-foreground mb-1">Subtitle</label>
            <input
              type="text"
              value={content.subtitle}
              onChange={(e) => handleChange('subtitle', e.target.value)}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-muted-foreground mb-1">Button Text</label>
              <input
                type="text"
                value={content.buttonText}
                onChange={(e) => handleChange('buttonText', e.target.value)}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-muted-foreground mb-1">Button Link</label>
              <input
                type="text"
                value={content.buttonLink}
                onChange={(e) => handleChange('buttonLink', e.target.value)}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-bold text-muted-foreground mb-1">Background Image URL</label>
            <input
              type="text"
              value={content.backgroundImage}
              onChange={(e) => handleChange('backgroundImage', e.target.value)}
              placeholder="https://..."
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        </div>
      );

    case 'text':
      return (
        <div>
          <label className="block text-xs font-bold text-muted-foreground mb-1">Content (HTML supported)</label>
          <textarea
            value={content.content}
            onChange={(e) => handleChange('content', e.target.value)}
            rows={6}
            className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary font-mono text-sm"
          />
        </div>
      );

    case 'image':
      return (
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-bold text-muted-foreground mb-1">Image URL</label>
            <input
              type="text"
              value={content.src}
              onChange={(e) => handleChange('src', e.target.value)}
              placeholder="https://..."
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-muted-foreground mb-1">Alt Text</label>
            <input
              type="text"
              value={content.alt}
              onChange={(e) => handleChange('alt', e.target.value)}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-muted-foreground mb-1">Caption</label>
            <input
              type="text"
              value={content.caption}
              onChange={(e) => handleChange('caption', e.target.value)}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        </div>
      );

    case 'cta':
      return (
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-bold text-muted-foreground mb-1">Title</label>
            <input
              type="text"
              value={content.title}
              onChange={(e) => handleChange('title', e.target.value)}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-muted-foreground mb-1">Description</label>
            <textarea
              value={content.description}
              onChange={(e) => handleChange('description', e.target.value)}
              rows={2}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-muted-foreground mb-1">Button Text</label>
              <input
                type="text"
                value={content.buttonText}
                onChange={(e) => handleChange('buttonText', e.target.value)}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-muted-foreground mb-1">Button Link</label>
              <input
                type="text"
                value={content.buttonLink}
                onChange={(e) => handleChange('buttonLink', e.target.value)}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>
        </div>
      );

    case 'faq':
      return (
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-bold text-muted-foreground mb-1">Section Title</label>
            <input
              type="text"
              value={content.title}
              onChange={(e) => handleChange('title', e.target.value)}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div className="space-y-3">
            {(content.items || []).map((item, index) => (
              <div key={index} className="p-3 bg-background rounded-lg border border-border">
                <div className="flex items-start gap-2">
                  <div className="flex-1 space-y-2">
                    <input
                      type="text"
                      value={item.question}
                      onChange={(e) => handleFaqUpdate(index, 'question', e.target.value)}
                      placeholder="Question"
                      className="w-full px-3 py-2 bg-card border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                    <textarea
                      value={item.answer}
                      onChange={(e) => handleFaqUpdate(index, 'answer', e.target.value)}
                      placeholder="Answer"
                      rows={2}
                      className="w-full px-3 py-2 bg-card border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <button
                    onClick={() => handleFaqDelete(index)}
                    className="p-2 text-muted-foreground hover:text-red-500 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
          <button
            onClick={handleFaqAdd}
            className="w-full py-2 border-2 border-dashed border-border hover:border-primary/50 rounded-lg text-muted-foreground hover:text-foreground transition-colors flex items-center justify-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Question
          </button>
        </div>
      );

    case 'quote':
      return (
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-bold text-muted-foreground mb-1">Quote</label>
            <textarea
              value={content.text}
              onChange={(e) => handleChange('text', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-muted-foreground mb-1">Author</label>
              <input
                type="text"
                value={content.author}
                onChange={(e) => handleChange('author', e.target.value)}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-muted-foreground mb-1">Role/Title</label>
              <input
                type="text"
                value={content.role}
                onChange={(e) => handleChange('role', e.target.value)}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>
        </div>
      );

    case 'video':
      return (
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-bold text-muted-foreground mb-1">Video URL (YouTube or Vimeo)</label>
            <input
              type="text"
              value={content.url}
              onChange={(e) => handleChange('url', e.target.value)}
              placeholder="https://youtube.com/watch?v=..."
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-muted-foreground mb-1">Title</label>
            <input
              type="text"
              value={content.title}
              onChange={(e) => handleChange('title', e.target.value)}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        </div>
      );

    case 'spacer':
      return (
        <div>
          <label className="block text-xs font-bold text-muted-foreground mb-1">Height (px)</label>
          <input
            type="number"
            value={content.height}
            onChange={(e) => handleChange('height', parseInt(e.target.value) || 0)}
            min={0}
            max={500}
            className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
      );

    case 'media-gallery':
      return (
        <MediaGalleryEditor content={content} handleChange={handleChange} />
      );

    case 'audio':
      return (
        <AudioBlockEditor content={content} handleChange={handleChange} />
      );

    case 'button-group':
      return (
        <ButtonGroupEditor content={content} handleChange={handleChange} />
      );

    case 'testimonials':
      return (
        <TestimonialsEditor content={content} handleChange={handleChange} />
      );

    case 'pricing':
      return (
        <PricingEditor content={content} handleChange={handleChange} />
      );

    case 'gift':
      return (
        <GiftCardEditor content={content} handleChange={handleChange} />
      );

    case 'event':
      return (
        <EventEditor content={content} handleChange={handleChange} />
      );

    case 'countdown':
      return (
        <CountdownEditor content={content} handleChange={handleChange} />
      );

    case 'social':
      return (
        <SocialLinksEditor content={content} handleChange={handleChange} />
      );

    case 'divider':
      return (
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-bold text-muted-foreground mb-1">Style</label>
            <select
              value={content.style}
              onChange={(e) => handleChange('style', e.target.value)}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="solid">Solid Line</option>
              <option value="dashed">Dashed Line</option>
              <option value="dotted">Dotted Line</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold text-muted-foreground mb-1">Width</label>
            <select
              value={content.width}
              onChange={(e) => handleChange('width', e.target.value)}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="full">Full Width</option>
              <option value="half">Half Width</option>
              <option value="quarter">Quarter Width</option>
            </select>
          </div>
        </div>
      );

    default:
      return <div className="text-sm text-muted-foreground">No editor available</div>;
  }
};

// Main Page Editor Component
export const AdminPageEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = window.location.pathname;
  const isNew = !id || location.endsWith('/new');
  
  const [page, setPage] = useState({
    title: '',
    slug: '',
    blocks: [],
    published: false
  });
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [editingBlock, setEditingBlock] = useState(null);
  const [showBlockPicker, setShowBlockPicker] = useState(false);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    if (!isAdminAuthenticated()) {
      navigate('/admin');
      return;
    }
    if (!isNew && id) {
      loadPage();
    }
  }, [id]);

  const loadPage = async () => {
    try {
      const response = await adminFetch(`/admin/pages/${id}`);
      const data = await response.json();
      setPage(data);
    } catch (error) {
      toast.error('Failed to load page');
      navigate('/admin/pages');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!page.title || !page.slug) {
      toast.error('Please fill in title and slug');
      return;
    }

    setSaving(true);
    try {
      const endpoint = isNew ? '/admin/pages' : `/admin/pages/${id}`;
      const method = isNew ? 'POST' : 'PUT';
      
      const response = await adminFetch(endpoint, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(page)
      });

      if (response.ok) {
        toast.success(isNew ? 'Page created!' : 'Page saved!');
        if (isNew) {
          const data = await response.json();
          navigate(`/admin/pages/edit/${data.id}`);
        }
      }
    } catch (error) {
      toast.error('Failed to save page');
    } finally {
      setSaving(false);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (active.id !== over.id) {
      const oldIndex = page.blocks.findIndex(b => b.id === active.id);
      const newIndex = page.blocks.findIndex(b => b.id === over.id);
      
      setPage({
        ...page,
        blocks: arrayMove(page.blocks, oldIndex, newIndex)
      });
    }
  };

  const addBlock = (type) => {
    const newBlock = {
      id: `block-${Date.now()}`,
      type,
      content: getDefaultContent(type)
    };
    
    setPage({
      ...page,
      blocks: [...page.blocks, newBlock]
    });
    setShowBlockPicker(false);
    setEditingBlock(newBlock.id);
  };

  const updateBlock = (blockId, updates) => {
    setPage({
      ...page,
      blocks: page.blocks.map(b => 
        b.id === blockId ? { ...b, ...updates } : b
      )
    });
  };

  const deleteBlock = (blockId) => {
    setPage({
      ...page,
      blocks: page.blocks.filter(b => b.id !== blockId)
    });
    if (editingBlock === blockId) {
      setEditingBlock(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background noise-texture">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-background/95 backdrop-blur border-b border-border">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/admin/pages')}
                className="p-2 text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
              <div>
                <input
                  type="text"
                  value={page.title}
                  onChange={(e) => setPage({ ...page, title: e.target.value })}
                  placeholder="Page Title"
                  className="text-2xl font-bold font-display bg-transparent text-foreground focus:outline-none"
                />
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-muted-foreground">/</span>
                  <input
                    type="text"
                    value={page.slug}
                    onChange={(e) => setPage({ ...page, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                    placeholder="page-slug"
                    className="text-sm bg-transparent text-muted-foreground focus:outline-none focus:text-foreground"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowTemplatePicker(true)}
                className="flex items-center gap-2 px-4 py-2 text-foreground hover:bg-card rounded-lg transition-colors border border-border"
              >
                <Layout className="w-4 h-4" />
                Templates
              </button>
              
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={page.published}
                  onChange={(e) => setPage({ ...page, published: e.target.checked })}
                  className="w-4 h-4 rounded border-border text-primary focus:ring-primary"
                />
                <span className="text-sm text-foreground">Published</span>
              </label>
              
              {!isNew && (
                <a
                  href={`/${page.slug === 'home' ? '' : page.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 text-foreground hover:bg-card rounded-lg transition-colors"
                >
                  <Eye className="w-4 h-4" />
                  Preview
                </a>
              )}
              
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-6 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full font-bold transition-all disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Blocks */}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={page.blocks.map(b => b.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-4">
              {page.blocks.map((block) => (
                <SortableBlock
                  key={block.id}
                  block={block}
                  onEdit={updateBlock}
                  onDelete={deleteBlock}
                  isEditing={editingBlock === block.id}
                  setEditingBlock={setEditingBlock}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>

        {/* Add Block Button */}
        <div className="mt-6">
          <button
            onClick={() => setShowBlockPicker(true)}
            className="w-full py-4 border-2 border-dashed border-border hover:border-primary/50 rounded-xl text-muted-foreground hover:text-foreground transition-all flex items-center justify-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Add Block
          </button>
        </div>
      </div>

      {/* Block Picker Modal */}
      <AnimatePresence>
        {showBlockPicker && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
            onClick={() => setShowBlockPicker(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-card border border-border rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-4 border-b border-border flex items-center justify-between">
                <h3 className="text-lg font-bold text-foreground">Add Content Block</h3>
                <button
                  onClick={() => setShowBlockPicker(false)}
                  className="p-2 hover:bg-background rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-foreground" />
                </button>
              </div>
              
              <div className="p-4 grid grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto">
                {BLOCK_TYPES.map((blockType) => {
                  const Icon = blockType.icon;
                  return (
                    <button
                      key={blockType.type}
                      onClick={() => addBlock(blockType.type)}
                      className="flex items-start gap-3 p-4 bg-background hover:bg-primary/10 border border-border hover:border-primary/50 rounded-xl text-left transition-all"
                    >
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium text-foreground">{blockType.label}</p>
                        <p className="text-xs text-muted-foreground">{blockType.description}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Template Picker */}
      <TemplatePicker
        isOpen={showTemplatePicker}
        onClose={() => setShowTemplatePicker(false)}
        onSelectTemplate={(templateBlocks) => {
          setPage(prev => ({
            ...prev,
            blocks: [...prev.blocks, ...templateBlocks]
          }));
        }}
        currentBlocks={page.blocks}
      />
    </div>
  );
};
